import { withErrorBoundary } from '@sentry/react';
import React from 'react';
import { Redirect, useHistory, useRouteMatch } from 'react-router-dom';
import { useQuery, useSubscription } from 'urql';
import { assets } from '../../assets';
import Button from '../../components/buttons/button';
import { BlurredContainerM2030 } from '../../components/containers/containers';
import DefaultScreen from '../../components/containers/default-screen';
import DataRow from '../../components/data-viewers/data-row/data-row';
import { Fallback } from '../../components/utils/error-boundary';
import { formatDateTime } from '../../helpers/date-formatters';
import { formatNumber } from '../../helpers/number-formatters';
import { StatusIcon, stringifyStatus } from '../../helpers/type-formatters';
import { GraphqlOperations } from '../../logic/graphql/operations';
import {
  CurrencyConversionListItem,
  Query,
  Status,
  Subscription,
} from '../../logic/graphql/types/types.d';
import { Paths } from '../../navigation/paths';
import { FormattedMessage, useIntl } from 'react-intl';

const CurrencyConversionDetails = (): JSX.Element => {
  const history = useHistory();
  const match = useRouteMatch();
  const intl = useIntl();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const id: string = (match.params as any).id || '';

  const [currencyConversionQueryResult, reexecuteCurrencyConversionQuery] = useQuery<Query>({
    query: GraphqlOperations.queries.get_currency_conversion.queryString,
    variables: GraphqlOperations.queries.get_currency_conversion.createVars(id),
    pause: id === '',
    requestPolicy: 'network-only',
  });
  const currencyConversion = currencyConversionQueryResult.data?.get_currency_conversion;

  const handleUpdateCurrencyConversionSubscription = (
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    subscriptionCurrencyConversions: CurrencyConversionListItem[] = [],
    response: Subscription,
  ): CurrencyConversionListItem[] => {
    if (response && response.update_currency_conversion) {
      if (
        response.update_currency_conversion.transaction_code ===
        currencyConversionQueryResult.data?.get_currency_conversion.transaction_code
      ) {
        reexecuteCurrencyConversionQuery();
      }
    }
    return [];
  };

  useSubscription(
    {
      query: GraphqlOperations.subscriptions.update_currency_conversion.subscriptionString,
    },
    handleUpdateCurrencyConversionSubscription,
  );

  const handleBackClick = (): void => {
    history.goBack();
  };

  const handleCancelClick = (): void => {
    history.push(Paths.contact);
  };

  if (id === '') {
    return <Redirect to={Paths.currency_conversions} />;
  }

  const exchangeRateDescriptor =
    (currencyConversion?.from_currency.currency || '') +
    ' / ' +
    (currencyConversion?.to_currency.currency || '');

  const title =
    currencyConversion?.from_currency.currency && currencyConversion?.to_currency.currency
      ? currencyConversion.from_currency.currency +
        ' - ' +
        currencyConversion.to_currency.currency +
        intl.formatMessage({ id: 'currencyConversions.details.titleCurrencyExtension' })
      : intl.formatMessage({ id: 'currencyConversions.details.defaultTitle' });
  const titleWithTransactionCode = currencyConversion?.transaction_code
    ? title + ' - ' + currencyConversion.transaction_code
    : title;

  return (
    <DefaultScreen
      background={assets.images.orders_bg}
      title={titleWithTransactionCode}
      onBackClick={handleBackClick}
      loading={currencyConversionQueryResult.fetching}
      rightContent={
        <aside className="flex flex-row items-center">
          <StatusIcon status={currencyConversion?.status} />
          <h2 className="ml-2">{stringifyStatus(currencyConversion?.status, intl)}</h2>
        </aside>
      }
    >
      <div className="grid row-gap-1 col-gap-3  grid-cols-1 lg:grid-cols-2 mb-6">
        <BlurredContainerM2030>
          <h2 className="mb-6">
            <FormattedMessage id="currencyConversions.details.dataLabel" />
          </h2>
          <DataRow
            label={intl.formatMessage({ id: 'currencyConversions.details.fromCurrencyLabel' })}
            value={currencyConversion?.from_currency.currency}
          />
          <DataRow
            label={intl.formatMessage({ id: 'currencyConversions.details.toCurrencyLabel' })}
            value={currencyConversion?.to_currency.currency}
          />
          <DataRow
            label={intl.formatMessage({ id: 'currencyConversions.details.fromAmountLabel' })}
            value={formatNumber(
              currencyConversion?.from_amount,
              currencyConversion?.from_currency.currency,
            )}
          />
          <DataRow
            label={intl.formatMessage({ id: 'currencyConversions.details.conversionDateLabel' })}
            value={formatDateTime(currencyConversion?.order_date)}
          />
        </BlurredContainerM2030>
        <BlurredContainerM2030>
          <h2 className="mb-6">
            <FormattedMessage id="currencyConversions.details.resultLabel" />
          </h2>

          <DataRow
            label={intl.formatMessage({ id: 'currencyConversions.details.toAmountLabel' })}
            value={formatNumber(
              currencyConversion?.to_amount,
              currencyConversion?.to_currency.currency,
            )}
          />
          <DataRow
            label={intl.formatMessage({ id: 'currencyConversions.details.exchangeRateLabel' })}
            value={formatNumber(currencyConversion?.exchange_rate, exchangeRateDescriptor)}
          />
          <DataRow
            label={intl.formatMessage({ id: 'currencyConversions.details.fulfillmentDateLabel' })}
            value={formatDateTime(currencyConversion?.fulfillment_date)}
          />
        </BlurredContainerM2030>
      </div>
      {currencyConversion?.status === Status.Active ? (
        <Button secondary onClick={handleCancelClick}>
          <FormattedMessage id="currencyConversions.details.cancelButton" />
        </Button>
      ) : null}
    </DefaultScreen>
  );
};

export default withErrorBoundary(CurrencyConversionDetails, { fallback: Fallback });
